import { Injectable } from '@angular/core';

import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { IReopenAvailableType } from '@shared/models/card-actions.model';
import { FormSelectOption } from '@shared/models/form-select.model';
import { NewCard, NewCardBranch } from '@shared/models/new-card.model';
import { Card, Product } from '@shared/models/products.model';

import { DocumentSetting } from '../payments/payment.model';
import { PublicDataService } from '../public-data/public-data.service';
import { UtilsService } from '../utils/utils.service';
import { ProductsApiService } from './products-api.service';

@Injectable({
  providedIn: 'root'
})
export class CardService {
  // Коды продуктов для моментальных карт
  private readonly MOMEMT_CARD_PRODUCT_CODES = ['CARD_DC_MIR_MOMENT', 'CARD_DC_VISA_MOMENT', 'CARD_DC_MC_MOMENT'];

  // Коды продуктов для карт, которые нельзя использовать как дополнительные
  private readonly IGNORE_CARD_PRODUCT_CODES_FOR_ADDITIONAL_CARD = [
    'CARD_DC_MIR_PRIMA',
    'CARD_DC_MIR_PRIVILEGE',
    'CARD_DC_MIR_MOMENT',
    'CARD_DC_VISA_MOMENT',
    'CARD_DC_MC_MOMENT'
  ];

  constructor(
    private productsApiService: ProductsApiService,
    private publicDataService: PublicDataService,
    private utilsService: UtilsService
  ) {}

  /**
   * Проверка на моментальную карту
   */
  public checkOnMomentCard(product: Product): boolean {
    return (
      product.productType === 'card' &&
      product.productCode &&
      this.MOMEMT_CARD_PRODUCT_CODES.includes(product.productCode)
    );
  }

  /**
   * Проверка возможности продукта для дополнительной карты
   */
  public checkProductOnAdditionalCard(product: Product, cardData: NewCard): boolean {
    return (
      !this.IGNORE_CARD_PRODUCT_CODES_FOR_ADDITIONAL_CARD.includes(product.productCode) &&
      product.isDebitCard &&
      !product.isVirtual &&
      cardData &&
      cardData.cardTagCode !== '1' &&
      product.status !== 'block'
    );
  }

  /**
   * Получение данных для блокировки карты
   */
  public getLockCardData(): Observable<{
    reasons?: FormSelectOption[];
    docSettings: DocumentSetting | DocumentSetting[];
  }> {
    return Observable.create((observer) => {
      this.productsApiService.lockCard({}).subscribe(
        (data: any) => {
          const setting = this.utilsService.getSetting(data.docSettings, 'USE_LOCK_CARD_REASONS');

          if (setting && setting.value === '1') {
            this.publicDataService.getServiceData('lock_card_reasons').subscribe(
              (lockCardReasons) => {
                const reasons: FormSelectOption[] = lockCardReasons.map(
                  (item: { caption: string; code: string; id: number }) => {
                    return {
                      value: item.code,
                      caption: item.caption
                    };
                  }
                );

                const result = {
                  docSettings: data.docSettings,
                  reasons
                };

                observer.next(result);
                observer.complete();
              },
              (err) => {
                observer.error(err);
                observer.complete();
              }
            );
          } else {
            observer.next({ docSettings: data.docSettings });
            observer.complete();
          }
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  /**
   * Получение данных для разблокировки карты
   */
  public getUnlockCardData(): Observable<{ docSettings: DocumentSetting | DocumentSetting[] }> {
    return this.productsApiService.unlockCard({}).pipe(map((data: any) => ({ docSettings: data.docSettings })));
  }

  /**
   * Получение данных для переоформления карты
   */
  public getReopenCardData(productCode: string): Observable<{
    docSettings: any[];
    cities: any[];
    availableTypes: IReopenAvailableType[];
    branches: NewCardBranch[];
  }> {
    return forkJoin(
      this.productsApiService.loadReopenCardData({}),
      this.publicDataService.getServiceData('city'),
      this.productsApiService.loadAvailableTypesForReopenCardByProductCode(productCode),
      this.productsApiService.getBranches()
    ).pipe(
      map(([res, cities, availableTypes, branches]) => {
        return {
          docSettings: (res as { docSettings }).docSettings,
          cities,
          availableTypes,
          branches
        };
      })
    );
  }

  /**
   * Получение причин переоформления карты
   */
  public getReopenCardReasons(codeMain: string, codeAdd: string): Observable<any[]> {
    return this.productsApiService.getReopenCardReasons(codeMain, codeAdd);
  }

  /**
   * Изменение PIN-кода карты
   */
  public changeCardPin(data: { cardId: string; encryptedPin: string }): Observable<any> {
    return this.productsApiService.changeCardPin(data);
  }

  /**
   * Получение сертификата для смены PIN-кода
   */
  public getCertificateForChangePin(): Observable<string> {
    return this.productsApiService.getCertificateForChangePin();
  }

  /**
   * Получение списка карт с дополнительными картами
   */
  public getCardsListWithAdditionalCards(products: Product[]): Card[] {
    const allCards: Card[] = products.filter((product) => product.productType === 'card') as Card[];
    const parentCards = allCards.filter((card) => card.generalCardNumber === card.cardNumber);
    const childrenCards = allCards.filter((child) => child.generalCardNumber !== child.cardNumber);
    let mappedCards = [];

    if (parentCards.length) {
      parentCards.forEach((parent) => {
        parent.additionalCards = childrenCards
          .filter((item) => item.generalCardNumber === parent.cardNumber)
          .map((card) => ({ ...card, showInList: parent.status === 'block' ? false : true }));
      });
      mappedCards = parentCards.reduce((acc, value) => {
        acc.push(value);
        if (value.additionalCards.length) {
          acc.push(...value.additionalCards);
        }

        return acc;
      }, []);
    } else {
      mappedCards = childrenCards.map((card) => ({ ...card, showInList: true }));
    }

    return mappedCards;
  }

  /**
   * Блокировка карты
   */
  public lockCard(data: any): Observable<any> {
    return this.productsApiService.lockCard(data);
  }

  /**
   * Разблокировка карты
   */
  public unlockCard(data: any): Observable<any> {
    return this.productsApiService.unlockCard(data);
  }
}
